import React, { Component } from 'react';

import Popper from 'popper.js';
import ContainerDimensions from 'react-container-dimensions'
import { A, R } from '../routes';

import Panic from '../Panic';
import MUTIL from '../MUTIL';
import Animator from '../Animator';
import I18N from '../I18N';
import { mk_svg_id_and_url, PresentingRectClipPath, LinearGradient, points2string, calc_text_width } from '../SVGUtil';
import { T, TraksConsumer } from '../traks';
import { Apify, ApifySerial, ApifyParallel, ApifyMap, ApifyAsyncConstructor } from '../Apify';
import ActionLink from './ActionLink';
import { StopWatch, DelayedCallback } from '../Time';
import { parse_date } from '../misc';
import { default_currency } from '../Site';
import { coalesce } from '../misc';
import hash from '../hash';

const mk_from_date_fn = (months_back) => {
	return () => {
		var d = new Date();
		d.setMonth(d.getMonth() - months_back);
		return d;
	};
};

const ranges = [
	{brief_text: <T>24h</T>, key: "1d", q0: "i1d", supports_update: true, is_intraday: true},
	{brief_text: <T>7d</T>,  key: "7d", q0: "i7d", supports_update: true, is_intraday: true},
	{brief_text: <T>1m</T>,  key: "1m", q0: "c1m", use_any: true, from_date_fn: mk_from_date_fn(1)},
	{brief_text: <T>3m</T>,  key: "3m", q0: "c3m", use_any: true, from_date_fn: mk_from_date_fn(3)},
	{brief_text: <T>1y</T>,  key: "1y", q0: "c1y", from_date_fn: mk_from_date_fn(12*1)},
	{brief_text: <T>5y</T>,  key: "5y", q0: "c5y", from_date_fn: mk_from_date_fn(12*5)},
];

const range_map = (() => {
	var map = {};
	for (const r of ranges) {
		map[r.key] = r;
	}
	return map;
})();

class Poppy {
	/* RATIONALE for raw popper.js usage: the DOM element which popper.js
	 * targets (points at) lies within an SVG which makes it awkward to use
	 * reactstrap's <Popover>-tag; additionally, React likes "Lifting State
	 * Up" (google it), which would imply refreshing the entire chart SVG
	 * (big) frequently because a parent HTML/React element must maintain
	 * the popper state (which would be parent to the entire SVG); the
	 * former approach avoided lifting state up by maintaining a pubsub
	 * link between the two child components (one maintaining the GUI
	 * inside the SVG, and the other maintaining the <Popover> outside the
	 * SVG), but this made React complain about setState() being fired at
	 * invalid times, and it generally felt fragile and wrong. Using
	 * Bootstrap's own popover() is also weird because it involves jQuery.
	 * So I finally opted for replicating popover()'s behavior using the
	 * raw DOM API */

	constructor(ref, options) {
		this.root = document.createElement('div');
		document.body.appendChild(this.root);

		var p_inner = document.createElement('div');
		p_inner.setAttribute("class", "adx_popover-inner");
		this.root.appendChild(p_inner);

		this.body = document.createElement('div');
		this.body.setAttribute("class", "adx_popover-body");
		p_inner.appendChild(this.body);

		var p_arrow = document.createElement('span');
		p_arrow.setAttribute('class', 'adx_arrow ad_chart_popover_arrow');
		this.root.appendChild(p_arrow);

		const on_update = (data) => {
			if (!this.root) return;
			this.root.setAttribute("class", "adx_popover adx_show ad_pointer_events_none ad_chart_popover adx_bs-popover-" + data.placement);
		};

		options = options || {};
		options.modifiers = { arrow: { element: p_arrow } }
		options.onCreate = options.onUpdate = on_update;

		this.popper = new Popper(ref, this.root, options);

		/* XXX there's something weird about Popper positioning,
		 * possibly due to React, or the way I glued those two
		 * together, or the way I immediately update the Popper after
		 * constructing it? The initial position is wrong, and the
		 * Popper-arrow is sometimes in a wrong position (it looks
		 * broken, like Popper is misbehaving for whatever reason).
		 * Doing this delayed popper.update() seems to "fix" it,... at
		 * the expense of sometimes displaying a very brief jumpy
		 * glitch (very brief = looks like 1 frame). I've tried putting
		 * the Popper construction inside the delay (and enqueuing any
		 * update(callback) calls), but this exhibits the exact same
		 * broken behaviour as NOT executing this setTimeout()-delayed
		 * popper.update(). OH [W/H]ELL */
		setTimeout(() => {
			if (this.popper) this.popper.update();
		}, 0);
	}

	update(callback) {
		this.body.innerHTML = "";
		callback(this.body);
		this.popper.update();
	}

	destroy() {
		this.root.parentNode.removeChild(this.root);
		this.popper.destroy();
		this.popper = undefined;
	}
}

class GUI extends Component {
	constructor(props) {
		super(props);
		this.state = {
			down_pos: null,
			anchor_pos: null,
			drag_pos: null,
			dup: null,
			cursor_x: null,
		};

		this.mouse_stopwatch_click = new StopWatch();
		this.mouse_delayed_callback_drag = new DelayedCallback();

		this.touch_stopwatch_tap_duration = new StopWatch();
		this.touch_stopwatch_tap_interval = new StopWatch();
		this.touch_delayed_callback_tap = new DelayedCallback();
		this.touch_delayed_callback_hold = new DelayedCallback();
	}

	mouse_stop() {
		if (this.state.dup) document.removeEventListener("mouseup", this.state.dup);
		this.setState({
			anchor_pos: null,
			drag_pos: null,
			dup: null
		});
	}

	destroy_drag_poppy() {
		if (this.drag_poppy) {
			this.drag_poppy.destroy();
			delete this.drag_poppy;
		}
	}

	destroy_cursor_poppy() {
		if (this.cursor_poppy) {
			this.cursor_poppy.destroy();
			delete this.cursor_poppy;
		}
	}

	componentWillUnmount() {
		this.mouse_stop();
		this.destroy_cursor_poppy();
		this.destroy_drag_poppy();
	}

	render() {
		const cfg = this.props.cfg;

		const x = this.props.x || 0;
		const y = this.props.y || 0;

		const x_dex = (xval) => {
			const n = this.props.points.length;
			return Math.max(0, Math.min(n-1, Math.round(MUTIL.rescale(xval, 0, this.props.width, 0, n-1))));
		};

		const snap_x = (xval) => {
			const n = this.props.points.length;
			const index = x_dex(xval);
			return (index/(n-1)) * this.props.width;
		};

		const get_mpos = (e) => {
			const bbox = e.currentTarget.getBoundingClientRect();
			return [e.clientX - bbox.left, e.clientY - bbox.top];
		};

		const duration_for_tap = 100;
		const duration_for_click = 250;
		const duration_for_double_tap = 333;
		const duration_for_hold = 1000;

		const up = (e) => {
			e.preventDefault();

			this.mouse_delayed_callback_drag.cancel();

			const down_duration = this.mouse_stopwatch_click.stop();
			if (down_duration !== undefined && down_duration < duration_for_click) this.props.on_toggle();
			this.mouse_stop();
			this.setState({down_pos: null});
		};

		const touch = (info, set_anchor) => {
			var new_state = {};
			const tpos = info.touchpos;

			if (set_anchor) {
				new_state.anchor_pos = tpos;
			}

			if (tpos) {
				new_state.cursor_x = tpos[0];
				new_state.drag_pos = tpos;
			} else {
				new_state.cursor_x = null;
			}

			if (!this.state.anchor_pos) {
				this.touch_delayed_callback_hold.start(duration_for_hold, ((tpos) => () => {
					this.setState({anchor_pos: tpos});
				})(tpos));
			}

			this.setState(new_state);
		};

		const get_touch_info = (e) => {
			var touchpos = undefined;
			if (e.touches.length === 1) {
				const bbox = e.currentTarget.getBoundingClientRect();
				const item = e.touches.item(0);
				touchpos = [item.clientX - bbox.left, item.clientY - bbox.top]
			}
			return {touchpos};
		};

		const touchstart = (e) => {
			this.touch_stopwatch_tap_duration.start();
			const tinfo = get_touch_info(e);
			this.touch_delayed_callback_tap.start(duration_for_tap, ((tinfo) => () => {
				touch(tinfo);
			})(tinfo));
			e.preventDefault();
		};

		const touchmove = (e) => {
			this.touch_delayed_callback_tap.cancel();
			touch(get_touch_info(e));
			e.preventDefault();
		};

		const touchend = (e) => {
			this.touch_delayed_callback_tap.cancel();
			this.touch_delayed_callback_hold.cancel();
			const tap_duration = this.touch_stopwatch_tap_duration.stop();
			const tap_interval = this.touch_stopwatch_tap_interval.stop();
			if (tap_duration !== undefined && tap_duration < duration_for_tap) {
				this.setState({cursor_x: null, anchor_pos: null, drag_pos: null});
				if (tap_interval === undefined) {
					this.touch_stopwatch_tap_interval.start();
				} else if (tap_interval < duration_for_double_tap) {
					this.props.on_toggle();
				}
			}
			this.setState({anchor_pos: null, drag_pos: null});
			e.preventDefault();
		};

		const move = (e) => {
			e.preventDefault();
			const mpos = get_mpos(e);

			var d = undefined;
			if (this.state.down_pos) d = MUTIL.v2_distance(this.state.down_pos, mpos);

			var set_state = {cursor_x: mpos[0]};
			if (this.state.anchor_pos || (d !== undefined && d > 10)) {
				if (!this.state.anchor_pos) {
					set_state.anchor_pos = this.state.down_pos;
					this.mouse_delayed_callback_drag.cancel();
				}
				set_state.drag_pos = mpos;
				this.mouse_stopwatch_click.stop();
			}
			this.setState(set_state);
		};

		const leave = () => {
			this.setState({cursor_x: null});
		};

		var down;
		if (!this.state.anchor_pos) {
			down = (e) => {
				this.mouse_stopwatch_click.start();
				const dup = () => { this.mouse_stop(); };
				const mpos = get_mpos(e);
				this.setState({
					down_pos: mpos,
					drag_pos: mpos,
					dup: dup
				});

				this.mouse_delayed_callback_drag.start(duration_for_click, ((mpos) => () => {
					this.setState({
						anchor_pos: mpos,
					})
				})(mpos));

				document.addEventListener("mouseup", dup);
				e.preventDefault();
			};
		}

		var drag_fillunder_element;
		var is_drag = false;
		var drag_x = x + snap_x(this.state.cursor_x);
		var drag_y = y;
		var drag_w = 0;
		var drag_h = this.props.height;
		var drag_i0 = undefined;
		var drag_i1 = undefined;
		if (this.state.anchor_pos && this.state.drag_pos) {
			is_drag = true;
			const from = this.state.anchor_pos;
			const to = this.state.drag_pos;
			const left = Math.min(from[0], to[0]);
			const right = Math.max(from[0], to[0]);
			const x0 = snap_x(left);
			const x1 = snap_x(right);
			const width = x1 - x0;

			drag_i0 = x_dex(left);
			drag_i1 = x_dex(right);

			drag_x = x+x0;
			drag_w = width+1;

			if (drag_i0 !== drag_i1) {
				var polygon_points = []
				for (var i = drag_i0; i <= drag_i1; i++) {
					polygon_points.push([
						this.props.data_index_x_position(i),
						this.props.data_point_y_position(this.props.points[i].value)
					]);
				}
				polygon_points.push([
					this.props.data_index_x_position(drag_i1),
					y+this.props.height
				]);
				polygon_points.push([
					this.props.data_index_x_position(drag_i0),
					y+this.props.height
				]);
				drag_fillunder_element = (
					<polygon
						points={points2string(polygon_points)}
						className={cfg.class_drag_fill1}
						fill={this.props.xgrad}
					/>
				);
			}
		}

		var cursor_element;
		var ccx = 0;
		var ccy = 0;
		var ccc = "ad_chart_cursor_dot_hide";
		if (this.state.cursor_x !== null && !this.state.anchor_pos) {
			const sx = snap_x(this.state.cursor_x);
			const lx = x + sx;
			const point_index = x_dex(this.state.cursor_x);
			const point = this.props.points[point_index];
			const value = point.value;
			const val_y = this.props.data_point_y_position(value);

			cursor_element = (
				<g>
					<line x1={lx} y1={y} x2={lx} y2={y+this.props.height} className={cfg.class_cursor_line}/>
					<circle cx={lx} cy={val_y} r="6.5" className="ad_chart_cursor_dot_outer"/>
				</g>
			);
			ccx = lx;
			ccy = val_y;
			ccc = "ad_chart_cursor_dot_inner";

			if (this.cursor_ref && !this.cursor_poppy) {
				this.cursor_poppy = new Poppy(this.cursor_ref, {placement: "right"});
			}
			if (this.cursor_poppy) {
				this.cursor_poppy.update((body) => {
					let ts_element = document.createElement('div');
					ts_element.setAttribute("class", "ad_chart_cursor_ts");
					ts_element.innerText = this.props.render_static(this.props.date_formatter(point.ts));
					body.appendChild(ts_element);

					let value_element = document.createElement('div');
					value_element.setAttribute("class", "ad_chart_cursor_value");
					value_element.innerText = this.props.render_static(I18N.floatval_unit(point.value, this.props.n_decimals, this.props.unit));
					body.appendChild(value_element);

					if (this.props.show_volume) {
						let dvol_element = document.createElement('div');
						dvol_element.setAttribute("class", "ad_chart_cursor_volume");
						let volume = point.dvolume || 0;
						dvol_element.innerText = this.props.render_static(<T>Volume: {volume}</T>);
						body.appendChild(dvol_element);
					}
				})
			}
		} else {
			this.destroy_cursor_poppy();
		}

		if (is_drag) {
			if (this.drag_ref && !this.drag_poppy) {
				this.drag_poppy = new Poppy(this.drag_ref, {placement: "top"});
			}
			if (this.drag_poppy) {
				this.drag_poppy.update((body) => {
					var container = document.createElement("div");
					body.appendChild(container);

					let points = this.props.points;

					if (drag_i0 === drag_i1) {
						var help = document.createElement("div");
						help.innerHTML = this.props.render_static(
							<div>
								<div className="ad_drag_horiz_icon mr-1"></div>
								<T>Drag to show development</T>
							</div>
						);
						container.appendChild(help);
					} else {
						container.setAttribute("class", "text-center");

						const p0 = points[drag_i0];
						const p1 = points[drag_i1];

						var line_ts = document.createElement("div");
						line_ts.setAttribute("class", "ad_chart_cursor_ts");
						container.appendChild(line_ts);

						const t0 = p0.ts;
						const t1 = p1.ts;
						const t0_str = this.props.render_static(this.props.date_formatter(t0));
						const t1_str = this.props.render_static(this.props.date_formatter(t1));

						const apnd = (txt, html) => {
							var e = document.createElement("span");
							if (txt) e.innerText = txt;
							if (html) e.innerHTML = html;
							line_ts.appendChild(e);
						}

						apnd(t0_str);
						apnd(null, " &mdash; ");
						apnd(t1_str);

						var line_value = document.createElement("div");
						line_value.setAttribute("class", "ad_chart_cursor_value");
						container.appendChild(line_value);

						const v0 = p0.value;
						const v1 = p1.value;
						const dv = v1 - v0;
						const dvpct = (v1/v0-1)*100;
						const dv_str = this.props.render_static(I18N.floatval_unit(dv, this.props.n_decimals, this.props.unit));
						const dvpct_str = this.props.render_static(I18N.floatval(dvpct, 2));

						var g = document.createElement('span');
						var gt = "";
						var gc;
						if (dv > 0) { /* XXX: Port to ChangeArrow */
							gc = "ad_growth_positive";
							gt = "⬆︎";
						} else if (dv < 0) {
							gc = "ad_growth_negative";
							gt = "⬇︎";
						} else {
							gc = "ad_growth_zero";
						}
						gt += `${dv_str} (${dvpct_str}%)`;
						g.setAttribute("class", gc)
						g.innerText = gt;

						line_value.appendChild(g);
					}

					if (this.props.show_volume) {
						let volume = 0;
						for (let i = drag_i0; i <= drag_i1; i++) {
							let v = points[i].dvolume;
							if (v) volume += v;
						}
						let volume_value = document.createElement("div");
						volume_value.setAttribute("class", "ad_chart_cursor_volume");
						container.appendChild(volume_value);
						volume_value.innerText = this.props.render_static(<T>Volume: {volume}</T>);
					}
				});
			}
		} else {
			this.destroy_drag_poppy();
		}

		const set_drag_ref = (ref) => {
			this.drag_ref = ref;
		}

		const set_cursor_ref = (ref) => {
			this.cursor_ref = ref;
		};

		return (
			<g>
				<rect
					x={drag_x}
					y={drag_y}
					width={drag_w}
					height={drag_h}
					className={cfg.class_drag_fill0}
					fill={this.props.xgrad}
					ref={set_drag_ref}
				/>
				{drag_fillunder_element}
				{cursor_element}
				<circle
					ref={set_cursor_ref}
					cx={ccx}
					cy={ccy}
					r="5"
					className={ccc}
					fill={this.props.xgrad}
				/>
				<rect
					x={x}
					y={y}
					width={this.props.width}
					height={this.props.height}
					opacity="0"
					onMouseDown={down}
					onMouseUp={up}
					onMouseMove={move}
					onMouseLeave={leave}
					onTouchStart={touchstart}
					onTouchMove={touchmove}
					onTouchEnd={touchend}
				/>
			</g>
		);
	}
}

function get_yticks(props) {
	const nil = {
		elements: null,
		n_decimals: 2,
		text_width: 0,
	};

	const cfg = props.cfg;
	const desired_y_ticks = cfg.desired_y_ticks;
	if (desired_y_ticks === undefined) return nil;

	const steps = [10, 20, 25, 50];
	var exp10 = -7;
	var dv;
	while (dv === undefined && exp10 < 20) {
		exp10++;
		for (const step of steps) {
			var dv_tmp = step * Math.pow(10, exp10);
			if (((props.v_max - props.v_min) / dv_tmp) <= desired_y_ticks) {
				dv = dv_tmp;
				break;
			}
		}
	}
	if (dv === undefined || dv <= 0) return nil;

	var n_decimals = -exp10;
	if (n_decimals < 0) n_decimals = 0;

	const v0 = ((props.v_min / dv) | 0) * dv;
	const v1 = (((props.v_max / dv) | 0) + 1) * dv;
	var v;

	const y1 = props.y_min;
	const y2 = props.y_max;

	var tick_texts = [];
	for (v = v0; v < v1; v += dv) {
		var y = props.data_point_y_position(v);
		if (y < y1 || y > y2) continue;
		tick_texts.push(props.render_static(I18N.floatval(v, n_decimals)));
	}

	const text_width = calc_text_width(cfg.class_ytick_text, tick_texts) + 10;

	const x1 = props.x_start + text_width;
	const x2 = props.x_max;

	var elements = [];
	for (v = v0; v < v1; v += dv) {
		const y = props.data_point_y_position(v);
		if (y < y1 || y > y2) continue;
		const key = `${y}_${v}`;
		elements.push(
			<line
				key={"line_"+key}
				x1={x1}
				y1={y}
				x2={x2}
				y2={y}
				className={cfg.class_ytick_line}
			/>
		);

		const text = I18N.floatval(v, n_decimals);

		elements.push(
			<text
				key={"text_"+key}
				x={x1-7}
				y={y}
				textAnchor="end"
				alignmentBaseline="middle"
				className={cfg.class_ytick_text}
			>
			{text}
			</text>
		);
	}

	return {elements, n_decimals, text_width};
}

function XTicks(props) {
	if (props.points.length < 3) return null;
	const indices = [
		0,
		Math.floor(props.points.length/2),
		props.points.length-1
	];
	return [0,1,2].map((i) => {
		const index = indices[i];
		const ts = props.render_static(props.date_formatter(props.points[index].ts));
		const x = props.data_index_x_position(index);
		const anchor = ["start", "middle", "end"][i]
		return (
			<text
				key={i}
				x={x}
				y={props.y}
				textAnchor={anchor}
				alignmentBaseline="hanging"
				className={props.cfg.class_xtick_text}
			>
				{ts}
			</text>
		);
	})
}

class ChartSVGImpl extends Component {
	constructor(props) {
		super(props);
		this.state = {
			y_range_to_zero_t: 0,
			y_range_to_zero: false,
		};
	}

	componentDidMount() {
		this.v_min_scale_animator = new Animator({
			value: 0,
			target: 0,
			speed: 1/0.2,
			callback: (y_range_to_zero_t) => {
				this.setState({y_range_to_zero_t});
			}
		});
	}

	componentWillUnmount() {
		if (this.v_min_scale_animator) this.v_min_scale_animator.kill();
	}

	render() {
		const cfg = this.props.cfg;

		if (!this.props.chart || this.props.chart.points.length < 2) {
			return <div className="p-2 font-small ad_gray_fg"><T>no chart data yet</T></div>;
		}

		const points = this.props.chart.points;
		const unit = this.props.chart.unit;

		let v_min = undefined;
		let v_max = undefined;
		let t_min = undefined;
		let t_max = undefined;
		let dvol_max = undefined;
		let has_value = false;
		let has_min_max = false;
		let has_dvol = false;
		let is_intraday = false;
		const get_date_id = (d) => d.getDate() + d.getMonth()*100 + d.getYear()*100*100;
		let last_date_id;
		let point_per_day_counter = 0;
		for (const point of points) {
			has_value = has_value || (point.value !== undefined);
			has_min_max = has_min_max || (point.min !== undefined && point.max !== undefined);
			has_dvol = has_dvol || point.dvolume !== undefined;

			/* calculate value bounds */
			if (has_value) {
				if (v_min === undefined || point.value < v_min) v_min = point.value;
				if (v_max === undefined || point.value > v_max) v_max = point.value;
			}

			if (has_min_max) {
				if (v_min === undefined || point.min < v_min) v_min = point.min;
				if (v_max === undefined || point.max > v_max) v_max = point.max;
			}

			if (has_dvol) {
				// minimum assumed to always be zero
				if (dvol_max === undefined || point.dvolume > dvol_max) dvol_max = point.dvolume;
			}

			/* calculate timestamp bounds */
			const ts = point.ts;
			const date_id = get_date_id(ts);
			if (date_id !== last_date_id) {
				point_per_day_counter = 0;
				last_date_id = date_id;
			} else {
				point_per_day_counter++;
				if (point_per_day_counter >= 2) is_intraday = true;
			}
			const t = ts.getTime() / 1000;
			if (t_min === undefined || t < t_min) t_min = t;
			if (t_max === undefined || t > t_max) t_max = t;
		}

		if (v_min === v_max) { v_min -= 1; v_max += 1; }

		v_min = MUTIL.lerp(MUTIL.easen(2, this.state.y_range_to_zero_t), v_min, 0);
		v_max = MUTIL.lerp(MUTIL.easen(2, this.state.y_range_to_zero_t), v_max, v_max * 1.25);

		const container_x = this.props.x || 0;
		const container_y = this.props.y || 0;
		const x_start = container_x + (cfg.margin_left || 0);
		const y_min = container_y + (cfg.margin_top || 0);
		const x_max = (container_x + this.props.width) - (cfg.margin_right || 0);
		const y_max = (container_y + this.props.height) - (cfg.margin_bottom || 0);

		const data_point_y_position_bounds = (v,vmin,vmax,b0,b1) => {
			return MUTIL.rescale(v, vmin, vmax, y_min + (y_max - y_min) * b0, y_min + (y_max - y_min) * b1);
		};

		const data_point_y_position = (v) => data_point_y_position_bounds(v, v_min, v_max, 0.95, 0.10);

		const yticks = get_yticks({render_static: this.props.render_static, cfg, x_start, y_min, x_max, y_max, data_point_y_position, v_min, v_max})
		const x_min = x_start + yticks.text_width;

		const width = x_max - x_min;
		const height = y_max - y_min;

		const data_index_x_position = (index) => {
			return MUTIL.rescale(index, 0, points.length-1, x_min, x_max);
		};

		const [clip_path_id, clip_path_url] = mk_svg_id_and_url();
		const [line_grad_id, line_grad_url] = mk_svg_id_and_url();
		const [fill_grad_id, fill_grad_url] = mk_svg_id_and_url();

		let polygon_fill_points;
		let class_fill_gradstop_x0;
		let class_fill_gradstop_x1;
		if (has_min_max) {
			polygon_fill_points = [];
			for (let i = 0; i < points.length; i++) {
				polygon_fill_points.push([
					data_index_x_position(i),
					data_point_y_position(points[i].max)
				]);
			}
			for (let i = points.length-1; i >= 0; i--) {
				polygon_fill_points.push([
					data_index_x_position(i),
					data_point_y_position(points[i].min)
				]);
			}
			class_fill_gradstop_x0 = cfg.class_line_gradstop_x0;
			class_fill_gradstop_x1 = cfg.class_line_gradstop_x1;
		} else if (cfg.fill_under) {
			polygon_fill_points = [];
			for (let i = 0; i < points.length; i++) {
				polygon_fill_points.push([
					data_index_x_position(i),
					data_point_y_position(points[i].value)
				]);
			}
			polygon_fill_points.push([
				data_index_x_position(points.length-1),
				data_point_y_position(0)
			]);
			polygon_fill_points.push([
				data_index_x_position(0),
				data_point_y_position(0)
			]);
			class_fill_gradstop_x0 = cfg.class_fill_gradstop_x0 || cfg.class_line_gradstop_x0;
			class_fill_gradstop_x1 = cfg.class_fill_gradstop_x1 || cfg.class_line_gradstop_x1;
		}

		let volume_histogram;
		if (has_dvol && dvol_max !== undefined) {
			let bars = [];

			// volume data tends to be somewhat "spiky", so remap
			// volume to [0;1]-range and raise it to a power
			const y_squash_pwr = 0.50;
			const y_b0 = 0.95;
			const y_b1 = 0.8143643;
			let get_dvol_y = (v) => data_point_y_position_bounds(Math.pow(v/dvol_max, y_squash_pwr), 0, 1, y_b0, y_b1);

			let y_bottom = get_dvol_y(0);

			const ideal_x_spacing = 5;
			const min_w = 2;
			let w = (data_index_x_position(1) - data_index_x_position(0)) - ideal_x_spacing;
			if (w < min_w) w = min_w;

			for (let i = 0; i < points.length; i++) {
				let point = points[i];
				let dvol = point.dvolume
				if (dvol === undefined) continue;
				let x = data_index_x_position(i);
				let y = get_dvol_y(dvol);

				bars.push(
					<rect
						key={i}
						x={x-w/2}
						y={y}
						width={w}
						height={y_bottom-y}
						className={cfg.class_volume_fill}
					/>
				);
			}

			volume_histogram = (<g clipPath={clip_path_url}>{bars}</g>);
		}

		let polygon_fill;
		if (polygon_fill_points) {
			polygon_fill = (
				<polygon
					points={points2string(polygon_fill_points)}
					className={cfg.class_drag_fill1}
					fill={fill_grad_url}
					clipPath={clip_path_url}
				/>
			);
		}

		let polyline_points = [];
		for (let i = 0; i < points.length; i++) {
			polyline_points.push([
				data_index_x_position(i),
				data_point_y_position(points[i].value)
			]);
		}

		const date_formatter = is_intraday ? cfg.intraday_date_formatter : cfg.eod_date_formatter;

		let gui_inside;
		if (cfg.gui && has_value) {
			const toggle = () => {
				this.setState((prev_state) => {
					const y_range_to_zero = !prev_state.y_range_to_zero;
					this.v_min_scale_animator.set_target(y_range_to_zero ? 1 : 0);
					return {y_range_to_zero};
				});
			};

			gui_inside = (
				<GUI
					render_static={this.props.render_static}
					cfg={cfg}
					points={points}
					data_index_x_position={data_index_x_position}
					data_point_y_position={data_point_y_position}
					x={x_min}
					y={y_min}
					width={width}
					height={height}
					on_toggle={toggle}
					date_formatter={date_formatter}
					xgrad={line_grad_url}
					n_decimals={yticks.n_decimals}
					unit={unit}
					show_volume={has_dvol}
				/>
			);
		}

		let presenting_rect_clip_path_duration = cfg.animated ? 0.3 : 0.0;

		let element = (
			<div>
				<svg className="ad_chart_svg" viewBox={`0 0 ${this.props.width} ${this.props.height}`}>
					<defs>
						<PresentingRectClipPath
							id={clip_path_id}
							direction="right"
							duration={presenting_rect_clip_path_duration}
							t0={this.props.t0}
							x1={x_min}
							y1={y_min}
							x2={x_max}
							y2={y_max}
						/>
						<LinearGradient
							id={line_grad_id}
							x1={x_min}
							y1={0}
							x2={x_max}
							y2={0}
							stops={[[0, "."+cfg.class_line_gradstop_x0], [1, "."+cfg.class_line_gradstop_x1]]}
						/>
						<LinearGradient
							id={fill_grad_id}
							x1={0}
							y1={y_min}
							x2={0}
							y2={y_max}
							stops={[[0, "."+class_fill_gradstop_x0], [1, "."+class_fill_gradstop_x1]]}
						/>
					</defs>
					<rect
						width="100%"
						height="100%"
						className={cfg.class_bg}
					/>
					{yticks.elements}
					<XTicks
						render_static={this.props.render_static}
						cfg={cfg}
						y={y_max + 10}
						points={points}
						data_index_x_position={data_index_x_position}
						date_formatter={date_formatter}
					/>
					{volume_histogram}
					{polygon_fill}
					<polyline
						points={points2string(polyline_points)}
						fill="none"
						stroke={line_grad_url}
						className={cfg.class_line}
						clipPath={clip_path_url}
					/>
					{gui_inside}
				</svg>
			</div>
		);


		return element;
	}
}

const ChartCfgDefault = {
	gui: false,
	animated: true,
	class_bg: "ad_chart_bg",
	class_line: "ad_chart_line",
	class_line_gradstop_x0: "ad_chart_line_gradstop_x0",
	class_line_gradstop_x1: "ad_chart_line_gradstop_x1",
	class_fill_gradstop_x0: "ad_chart_fill_gradstop_x0",
	class_fill_gradstop_x1: "ad_chart_fill_gradstop_x1",
	class_ytick_line: "ad_chart_ytick_line",
	class_ytick_text: "ad_chart_ytick_text",
	class_xtick_text: "ad_chart_xtick_text",
	class_cursor_line: "ad_chart_cursor_line",
	class_drag_fill0: "ad_chart_drag_fill0",
	class_drag_fill1: "ad_chart_drag_fill1",
	class_volume_fill: "ad_chart_volume_fill",
	intraday_date_formatter: I18N.datetime_short,
	eod_date_formatter: I18N.date,
};

const ChartCfgShortDateMixin = {
	intraday_date_formatter: I18N.datetime_shorter,
	eod_date_formatter: I18N.date_short,
};

const ChartCfgSmall = {
	...ChartCfgDefault
};

const ChartCfgCard = {
	...ChartCfgSmall,
	fill_under: true,
}

const ChartCfgLarge = {
	...ChartCfgDefault,
	gui: true,
	class_bg: "ad_chart_large_bg",
	desired_y_ticks: 6,
	margin_left: 0,
	margin_right: 0,
	margin_top: 30,
	margin_bottom: 40,
	fill_under: true,
};

const ChartCfgStaticMixin = {
	gui: false,
	animated: false,
};

const ChartSVG = (props) => <TraksConsumer><ChartSVGImpl {...props}/></TraksConsumer>;

class Chart extends Component {
	constructor(props) {
		super(props);
		this.state = {t0: (new Date()).getTime()/1000};
	}

	render() {
		return (
			<div className="p-0" style={{"width":"100%","height":"100%"}}>
				<ContainerDimensions>
					<ChartSVG
						cfg={this.props.cfg}
						chart={this.props.chart_data}
						t0={this.state.t0}
					/>
				</ContainerDimensions>
			</div>
		);
	}
}

function zesc_get_chart_points(enc) {
	let ts_cursor;
	let skip;
	const inc = enc.Increment;

	switch (enc.TimeUnit) {
	case "day":
		const [year, month, day] = enc.BeginsAt.split("-").map(x => parseInt(x, 10))
		ts_cursor = new Date(year, month-1, day);
		skip = (n) => {
			const new_date = ts_cursor.getDate() + n * inc;
			ts_cursor = (new Date(ts_cursor.getTime()));
			ts_cursor.setDate(new_date);
		};
		break;
	case "minute":
		ts_cursor = new Date(enc.BeginsAt*1000);
		skip = (n) => {
			const inc_seconds = n * inc * 60;
			ts_cursor = new Date(ts_cursor.getTime() + inc_seconds * 1000);
		};
		break;
	default:
		Panic(`unhandled unit ${enc.TimeUnit}`);
	}

	const attr_count = enc.AttributeCount;
	const attrs = enc.AttributesFormat.split(",");
	if (attrs.length !== attr_count) {
		Panic(`attr mismatch: format=${enc.AttributesFormat} vs AttributeCount=${enc.AttributeCount}`);
	}

	const values = enc.Data || [];
	let points = [];
	let i = 0;
	let point;

	while (i < values.length) {
		const v = values[i];
		if (v !== 0) {
			point = {ts: ts_cursor};
			for (let j = 0; j < attr_count; j++) {
				point[attrs[j]] = values[i+j];
			}
			points.push(point);
			skip(1);
			i += attr_count;
		} else {
			const det = values[i+1];
			if (det === 0) {
				point = {ts: ts_cursor};
				for (let j = 0; j < attr_count; j++) {
					point[attrs[j]] = 0;
				}
				points.push(point);
				skip(1);
				i += 2;
			} else if (det > 0) {
				for (let n = 0; n < det; n++) {
					point = {ts: ts_cursor};
					for (let j = 0; j < attr_count; j++) {
						point[attrs[j]] = values[i+2+j];
					}
					points.push(point);
					skip(1);
				}
				i += 2 + attr_count;
			} else if (det < 0) {
				skip(-det);
				i += 2;
			} else {
				Panic(`expected number at index ${i+1}; got ${det}`);
			}
		}
	}

	if (i !== values.length) Panic(`expected i === values.length (${values.length}); but i === ${i}`);

	return points;
};

function identity_get_chart_points(enc) {
	const attrs = enc.AttributesFormat.split(",");
	var points = [];
	var tss = [new Date(enc.T0*1000), new Date(enc.T1*1000)]
	for (var i = 0; i < 2; i++) {
		var point = {ts: tss[i]};
		for (const attr of attrs) point[attr] = 1.0;
		points.push(point);
	}
	return points;
};

function get_chart_points(chart_data) {
	if (!chart_data) return [];
	const enc = chart_data.Encoded;
	switch (chart_data.Encoding) {
	case "ZESC": return zesc_get_chart_points(enc);
	case "IDENTITY": return identity_get_chart_points(enc);
	default: Panic("unhandled chart encoding: " + chart_data.Encoding);
	}
}

function merge_chart_points(pss) {
	if (pss.length === 0) return [];
	if (pss.length === 1) return pss[0];

	var timestamp_values_map = {};
	var known_keys_set = {};

	for (const ps of pss) {
		for (const p of ps) {
			var ts = p.ts.getTime();
			if (timestamp_values_map[ts] === undefined) {
				timestamp_values_map[ts] = {};
			}
			for (const ek of Object.keys(p)) {
				if (ek === "ts") continue;
				if (timestamp_values_map[ts][ek] !== undefined) Panic(`key collision for ${ek}`);
				timestamp_values_map[ts][ek] = p[ek];
				known_keys_set[ek] = true;
			}
		}
	}

	var timestamps = Object.keys(timestamp_values_map).map(ts => parseInt(ts, 10)).sort();

	const known_keys = Object.keys(known_keys_set);
	var merged_points = [];
	for (const ts of timestamps) {
		const values = timestamp_values_map[ts];
		var all_there = true;
		for (const key of known_keys) {
			if (values[key] === undefined) {
				all_there = false;
				break;
			}
		}
		if (!all_there) continue;
		merged_points.push({ts: new Date(ts), ...values});
	}

	return merged_points;
};

const mk_q = (type, key, props, unit) => {
	var range = props.range;
	if (range === undefined) range = "3m";
	const range_obj = range_map[range];
	if (range_obj === undefined) Panic(`invalid range ${range}`);

	var usage = props.usage;
	if (usage === undefined) usage = "full";
	if (usage !== "full" && usage !== "item") Panic(`invalid usage $(usage)`);

	var usage_str = range_obj.use_any ? "any" : usage;

	const is_update = false; // XXX FIXME TODO
	if (is_update && range_obj.supports_update) {
		usage_str += "update";
	}

	var parts = [type, range_obj.q0, usage_str];

	if (unit) parts.push("unit=" + unit);

	return parts.join(".") + "(" + key + ")";
};

const mk_stock_key = (props) => {
	return `${props.exchange.toUpperCase()}~${props.ticker.toUpperCase()}`;
};

const mk_stock_q = (props) => {
	var unit = null;
	if (props.exchange === "BANK") {
		unit = default_currency;
	}
	return mk_q("s", mk_stock_key(props), props, unit);
};

class _Chartify extends ApifyAsyncConstructor {
	constructor(type, opts) {
		super();
		this.opts = opts || {};
		this.type = type;
		this.usage = null;
		this.with_chart_component_prop();
	}

	with_chart_component_prop(name = "ChartComponent") {
		this.chart_component_prop = name;
		return this;
	}

	with_usage(usage) {
		this.usage = usage;
		return this;
	}

	use_api_refresh() {
		return coalesce(this.opts.refresh, true);
	}

	construct_async_impl() {
		const mk_chart_source = (q_from_props, response_prop = "chart_data") => {
			let apify = Apify("/z/chart");
			if (this.use_api_refresh()) {
				apify = apify.with_api_refresh();
			}

			return apify
				.with_4xx_prop("chart_error")
				.with_query_from_props((props) => {
					if (this.usage) {
						props = {
							usage: this.usage,
							...props
						};
					}
					return {q: q_from_props(props)}
				})
				.with_response_prop(response_prop);
		};

		var sources = [];
		var chart_data_props = ["chart_data"];
		switch (this.type) {
		case "stock":
			sources.push(mk_chart_source((props) => mk_stock_q(props)));
			break;
		case "portfolio":
			sources.push(mk_chart_source((props) => mk_q("p", props.id, props)));
			break;
		case "game":
			sources.push(mk_chart_source((props) => mk_q("p", props.portfolio_id, props)));
			sources.push(mk_chart_source((props) => mk_q("g", props.game_id, props), "game_minmax_chart_data"));
			chart_data_props.push("game_minmax_chart_data");
			break;
		case "game_no_portfolio":
			sources.push(mk_chart_source((props) => mk_q("g", props.game_id, props)));
			break;
		default:
			throw new Error("invalid type: " + this.type);
		}

		return ApifySerial(
			ApifyParallel(sources),
			ApifyMap((props) => {

				var result = {}

				var chart_points = merge_chart_points(
					chart_data_props.map((name) => get_chart_points(props[name]))
				);

				const chart_data = props.chart_error
					? {
						unit: "N/A",
						points: [],
					}
					: {
						unit: props[chart_data_props[0]].Unit,
						points: chart_points,
					};
				result.chart_data = chart_data;

				if (this.chart_component_prop) {
					result[this.chart_component_prop] = (props) => <Chart chart_data={chart_data} {...props}/>;
				}

				return result;
			})
		);
	}
}

function Chartify(type, opts) { return new _Chartify(type, opts); };

class LargeChartInner extends Component {
	static get static_name() { return "LargeChartInner"; }

	render() {
		const { ctrls, info, ChartComponent } = this.props;

		let cfg_desktop = {...ChartCfgLarge};
		let cfg_mobile = {...ChartCfgLarge, ChartCfgShortDateMixin};

		if (this.props.static) {
			cfg_desktop = {...cfg_desktop, ...ChartCfgStaticMixin};
			cfg_mobile = {...cfg_mobile, ...ChartCfgStaticMixin};
		}

		return (
			<div className="py-4 px-3">
				<div className="d-flex">
					{info}
					<div className="d-none d-sm-block ml-auto">{ctrls}</div>
				</div>
				<div className="ad_chart_large_outer d-none d-sm-block">
					<div className="ad_chart_large_inner">
						<ChartComponent cfg={cfg_desktop}/>
					</div>
				</div>
				<div className="ad_chart_large_outer ad_chart_large_outer_smalldevice d-block d-sm-none">
					<div className="ad_chart_large_inner">
						<ChartComponent cfg={cfg_mobile}/>
					</div>
				</div>
				<div className="d-block d-sm-none text-center">{ctrls}</div>
			</div>
		);
	}
}

const get_chartified = (() => {
	var cache = {};
	return (type, wrapped_class, opts) => {
		const wrapped_name = wrapped_class.static_name;
		const key = type + ":" + wrapped_name + ":" + hash(opts);
		if (!cache[key]) cache[key] = Chartify(type, opts).wrap(wrapped_class);
		return cache[key];
	};
})();

const TickerNotFound = (props) => {
	return <div>not found</div>;
};

class RangedChartImpl extends Component {
	constructor(props) {
		super(props);
		this.state = {range: null};
	}

	get_range() {
		var range;
		switch (this.props.range_in) {
		case "state":
			range = this.state.range;
			break;
		case "query":
			range = R.get_arg("range");
			break;
		default:
			throw new Error("invalid props.range_in value: " + this.props.range_in);
		}
		return range || this.props.range || "3m";
	}

	render() {
		if (this.props.error) return <TickerNotFound/>

		var current_range = this.get_range();
		var ctrls = null;

		if (!this.props.static) {
			const { avail } = this.props;
			const earliest = parse_date(avail.EarliestEODDataPoint);

			var available_ranges = [];
			var range_available = false;
			for (const range of ranges) {
				var exit = false;
				if (range.is_intraday) {
					if (!avail.HasIntraday) continue;
				} else if (range.from_date_fn) {
					const from_date = range.from_date_fn();
					if (earliest != null && from_date <= earliest) exit = true;
				}
				available_ranges.push(range);
				if (range.key === current_range) range_available = true;
				if (exit) break;
			}

			if (!range_available) current_range = available_ranges[available_ranges.length-1].key;

			ctrls = available_ranges.map((range) => {
				var inner;
				const txt = range.brief_text;
				if (range.key === current_range) {
					inner = <span className="ad_range_link_selected">{txt}</span>;
				} else {
					if (this.props.range_in === "state") {
						const click = () => { this.setState({range: range.key}); }
						inner = <ActionLink className="ad_range_link ad_no_outline_focus" on_click={click}>{txt}</ActionLink>;
					} else if (this.props.range_in === "query") {
						const href = R.patch({range: range.key});
						inner = <A className="ad_range_link ad_no_outline_focus" href={href}>{txt}</A>;
					} else {
						throw new Error("unhandled props.range_in value: " + this.props.range_in);
					}
				}
				return <span key={range.key} className="pl-4">{inner}</span>;
			});
		}

		const opts = {
			refresh: !this.props.static,
		};

		const Chartified = get_chartified(this.props.type, this.props.inner, opts);
		return <Chartified {...this.props} ctrls={ctrls} range={current_range}/>;
	}
}

const RangedChart = Apify("/z/chart_availability")
	.with_4xx_prop("error")
	.with_query_from_props((props) => {
		var q;
		switch (props.type) {
		case "stock":
			q = mk_stock_q(props);
			break;
		case "portfolio":
			q = mk_q("p", props.id, props);
			break;
		case "game":
			q = mk_q("p", props.portfolio_id, props);
			break;
		case "game_no_portfolio":
			q = mk_q("g", props.game_id, props);
			break;
		default:
			throw new Error("invalid type: " + props.type);
		}
		return {q};
	})
	.with_response_prop("avail")
	.wrap(RangedChartImpl);

export {
	Chart,
	RangedChart,
	LargeChartInner,
	Chartify,
	ChartCfgDefault,
	ChartCfgStaticMixin,
	ChartCfgSmall,
	ChartCfgCard,
	ChartCfgLarge,
}



// WEBPACK FOOTER //
// ./src/c/Chart.js